import React, { useEffect, useState } from "react";
import api from "../services/api";

function VersionConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text }

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await api.get("/version-config");
      setConfig(res.data.config);
    } catch (error) {
      console.log(error);
      setMessage({ type: "error", text: "Failed to load configuration" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (app, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [app]: {
        ...prev[app],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const res = await api.put("/version-config", {
        userApp: config.userApp,
        driverApp: config.driverApp,
      });
      setConfig(res.data.config);
      setMessage({ type: "success", text: "Configuration updated successfully" });
    } catch (error) {
      console.log(error);
      setMessage({ type: "error", text: "Failed to update configuration" });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (loading) return <div style={styles.loading}>Loading configuration...</div>;
  if (!config) return <div style={styles.loading}>No configuration found</div>;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <h1 style={styles.title}>Version & Maintenance</h1>
        <p style={styles.subtitle}>Control app versions and maintenance mode</p>
      </div>

      {message && (
        <div
          style={{
            ...styles.banner,
            background: message.type === "success" ? "#dcfce7" : "#fee2e2",
            color: message.type === "success" ? "#166534" : "#991b1b",
          }}
        >
          {message.text}
        </div>
      )}

      <div style={styles.grid}>
        <AppConfigCard
          title="User App"
          accent="#6366f1"
          data={config.userApp || {}}
          onChange={(field, value) => handleChange("userApp", field, value)}
        />
        <AppConfigCard
          title="Driver App"
          accent="#3b82f6"
          data={config.driverApp || {}}
          onChange={(field, value) => handleChange("driverApp", field, value)}
        />
      </div>

      <div style={styles.footer}>
        <button
          style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {config.lastUpdated && (
        <div style={styles.lastUpdated}>
          Last updated: {new Date(config.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function AppConfigCard({ title, accent, data, onChange }) {
  return (
    <div style={{ ...styles.card, borderLeft: `6px solid ${accent}` }}>
      <h3 style={styles.cardTitle}>{title}</h3>

      <div style={styles.field}>
        <label style={styles.label}>Latest Version</label>
        <input
          style={styles.input}
          type="text"
          value={data.latestVersion || ""}
          onChange={(e) => onChange("latestVersion", e.target.value)}
          placeholder="e.g. 1.0.0"
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Minimum Required Version</label>
        <input
          style={styles.input}
          type="text"
          value={data.minRequiredVersion || ""}
          onChange={(e) => onChange("minRequiredVersion", e.target.value)}
          placeholder="e.g. 1.0.0"
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Update URL</label>
        <input
          style={styles.input}
          type="text"
          value={data.updateUrl || ""}
          onChange={(e) => onChange("updateUrl", e.target.value)}
          placeholder="https://play.google.com/store/apps/details?id=..."
        />
      </div>

      <div style={styles.checkboxRow}>
        <input
          type="checkbox"
          id={`${title}-force`}
          checked={!!data.forceUpdate}
          onChange={(e) => onChange("forceUpdate", e.target.checked)}
        />
        <label htmlFor={`${title}-force`} style={styles.checkboxLabel}>
          Force Update
        </label>
      </div>

      <div style={styles.checkboxRow}>
        <input
          type="checkbox"
          id={`${title}-maintenance`}
          checked={!!data.isMaintenance}
          onChange={(e) => onChange("isMaintenance", e.target.checked)}
        />
        <label htmlFor={`${title}-maintenance`} style={styles.checkboxLabel}>
          Maintenance Mode
        </label>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Maintenance Message</label>
        <textarea
          style={styles.textarea}
          value={data.maintenanceMessage || ""}
          onChange={(e) => onChange("maintenanceMessage", e.target.value)}
          placeholder="We're currently updating our systems..."
          rows={3}
        />
      </div>
    </div>
  );
}

export default VersionConfig;

/* ================= STYLES ================= */

const styles = {
  page: {
    padding: "30px",
    background: "#f1f5f9",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "5px 0 0 0",
    color: "#64748b",
    fontSize: "15px",
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "20px",
    width: "fit-content",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "20px",
    marginBottom: "30px",
  },
  card: {
    background: "#ffffff",
    padding: "25px",
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
  },
  cardTitle: {
    margin: "0 0 20px 0",
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },
  field: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
    resize: "vertical",
    fontFamily: "'Inter', sans-serif",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  checkboxLabel: {
    fontSize: "14px",
    color: "#334155",
    fontWeight: "500",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  saveBtn: {
    background: "#6366f1",
    color: "#fff",
    border: "none",
    padding: "12px 28px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
  },
  lastUpdated: {
    marginTop: "16px",
    fontSize: "12px",
    color: "#94a3b8",
    textAlign: "right",
  },
  loading: {
    padding: "100px",
    textAlign: "center",
    fontSize: "20px",
    color: "#6366f1",
    fontWeight: "bold",
  },
};